import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { apiGet } from './utils/apiRequest';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ProfessionalPortal from './components/ProfessionalPortal';
import IndividualPortal from './components/IndividualPortal';
import FreeCheckup from './components/FreeCheckup';
import CheckoutSuccess from './components/CheckoutSuccess';
import DeepAnalysisResults from './components/DeepAnalysisResults';
import ResumeDeepAnalysis from './components/ResumeDeepAnalysis';
import ResultsCheckup from './components/ResultsCheckup';
import AuthCallback from './components/AuthCallback';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import './App.css';

import { BACKEND_URL } from "./utils/apiBase";
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Use apiGet which handles 401 and token refresh automatically
        const userData = await apiGet('/api/auth/me');
        setUser(userData);
      } catch (error) {
        // If error, user will be logged out by apiRequest wrapper
        console.error('Auth check failed:', error);
      }
    }
    setLoading(false);
  };

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const authValue = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={authValue}>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/free-checkup" element={<FreeCheckup />} />
            <Route path="/checkout-success" element={<CheckoutSuccess />} />
            <Route path="/deep-analysis/:analysisId" element={<DeepAnalysisResults />} />
            <Route path="/resume-deep-analysis/:checkupId" element={<ResumeDeepAnalysis />} />
            <Route path="/results-checkup/:checkupId" element={<ResultsCheckup />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            
            {/* Protected Routes */}
            <Route 
              path="/professional/*" 
              element={
                <ProtectedRoute requiredRole="professional">
                  <ProfessionalPortal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/individual/*" 
              element={
                <ProtectedRoute requiredRole="individual">
                  <IndividualPortal />
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;